import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { UserContext } from "../context/UserContext";
import { useNavigate, Link } from "react-router-dom";
import "./Completed.css"; // ✨

const Completed = () => {
  const { user } = useContext(UserContext);
  const [completed, setCompleted] = useState([]);
  const [userReviews, setUserReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("last-added"); // 🆕

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState(null);
  const [authorName, setAuthorName] = useState("");
  const [bookTitle, setBookTitle] = useState("");
  const [review, setReview] = useState("");
  const [reviewTitle, setReviewTitle] = useState("");
  const [rating, setRating] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const fetchCompleted = async () => {
      try {
        const res = await axios.get(
          `https://bookmatchup.onrender.com/api/lists/completed/${user.id}`
        );
        setCompleted(res.data);
      } catch (err) {
        console.error("Error loading completed list:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserReviews = async () => {
      try {
        const res = await axios.get(
          `https://bookmatchup.onrender.com/api/reviews/user/${user.id}`
        );
        setUserReviews(res.data);
      } catch (err) {
        console.error("Failed to fetch user reviews:", err);
      }
    };

    fetchCompleted();
    fetchUserReviews();
  }, [user, navigate]);

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  const handleOpenReviewModal = (bookId, author, title) => {
    if (!bookId) return;
    setSelectedBookId(bookId);
    setAuthorName(author);
    setBookTitle(title);
    setReview("");
    setReviewTitle("");
    setRating(0);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBookId(null);
    setReview("");
    setReviewTitle("");
    setRating(0);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (rating === 0) {
      alert("Please select a rating.");
      return;
    }

    const alreadyReviewed = userReviews.find(
      (r) => r.bookId === selectedBookId
    );
    if (alreadyReviewed) {
      alert("You have already reviewed this book.");
      return;
    }

    try {
      await axios.post("https://bookmatchup.onrender.com/api/review", {
        userId: user.id,
        bookId: selectedBookId,
        reviewText: review,
        reviewTitle,
        rating,
        author_name: authorName,
        title: bookTitle,
      });

      alert("Review submitted!");
      handleCloseModal();
    } catch (err) {
      console.error("Error submitting review:", err);
      alert("Failed to submit review.");
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Remove this book from your completed list?")) return;

    try {
      await axios.delete(
        `https://bookmatchup.onrender.com/api/lists/completed/${itemId}`
      );
      setCompleted((prev) => prev.filter((item) => item._id !== itemId));
    } catch (err) {
      console.error("Failed to delete item:", err);
    }
  };

  const sortCompleted = (list) => {
    switch (sortOrder) {
      case "first-added":
        return [...list]; // Oldest first
      case "a-z":
        return [...list].sort((a, b) => {
          const titleA = a.bookId?.title?.toLowerCase() || "";
          const titleB = b.bookId?.title?.toLowerCase() || "";
          return titleA.localeCompare(titleB);
        });
      case "last-added":
      default:
        return [...list].reverse(); // 🔥 Newest first
    }
  };

  if (loading) return <p>Loading completed books...</p>;

  const sortedCompleted = sortCompleted(completed);

  return (
    <div className="completed-container">
      <h2>Your Completed Books</h2>

      {/* 🆕 Sort Buttons */}
      <div className="sort-buttons-wrapper">
        <button
          className={`sort-button ${
            sortOrder === "last-added" ? "active" : ""
          }`}
          onClick={() => setSortOrder("last-added")}
        >
          Last Added
        </button>
        <button
          className={`sort-button ${
            sortOrder === "first-added" ? "active" : ""
          }`}
          onClick={() => setSortOrder("first-added")}
        >
          First Added
        </button>
        <button
          className={`sort-button ${sortOrder === "a-z" ? "active" : ""}`}
          onClick={() => setSortOrder("a-z")}
        >
          A to Z
        </button>
      </div>
      <p className="completed-note">Click a title to view book details.</p>

      {sortedCompleted.length === 0 ? (
        <p>No books in your completed list yet.</p>
      ) : (
        <ul className="completed-list">
          {sortedCompleted.map((book) => (
            <li key={book._id} className="completed-item">
              {book.cover_url && (
                <div className="cover-container">
                  <img
                    src={book.cover_url}
                    alt="Book Cover"
                    className="book-cover"
                  />
                </div>
              )}
              <div className="completed-details">
                <Link
                  to={`/book/${encodeURIComponent(book.bookId?.itemId || "")}`}
                  className="completed-title"
                >
                  {book.bookId?.title || "Untitled"}
                </Link>
                <p className="complete-author">
                  {book.author_name || "Unknown Author"}
                </p>
              </div>

              <div className="button-group">
                <button
                  className="button-danger"
                  onClick={() => handleDelete(book._id)}
                >
                  Remove
                </button>
                <button
                  className="button-style"
                  onClick={() =>
                    handleOpenReviewModal(
                      book.bookId?.itemId,
                      book.author_name,
                      book.bookId?.title
                    )
                  }
                >
                  Add Review
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* ✨ Review Modal */}
      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={handleCloseModal}>
              &times;
            </span>
            <h2 className="modal-title">Review: {bookTitle}</h2>

            <form onSubmit={handleSubmitReview}>
              <div className="rating-container">
                {[...Array(5)].map((_, i) => (
                  <span
                    key={i}
                    className={i + 1 <= rating ? "star-filled" : "star"}
                    onClick={() => setRating(i + 1)}
                  >
                    &#9733;
                  </span>
                ))}
              </div>

              <input
                type="text"
                value={reviewTitle}
                onChange={(e) => setReviewTitle(e.target.value)}
                placeholder="Review Title (optional)"
                maxLength={50}
                className="review-title-input"
              />
              <div className="char-counter">{reviewTitle.length}/50</div>

              <textarea
                className="review-textarea"
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder="Write your review here (optional)..."
                maxLength={500}
              />
              <div className="char-counter">{review.length}/500</div>

              <button type="submit" className="button-danger">
                Submit Review
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Completed;
